import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Paper, CircularProgress, InputBase } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import {
    selectProposablePrivateEmployee,
    updateCheckedProposableShifts,
    updateProposalPartialDates,
} from '../../actions';

import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList  } from 'react-window';
import ProposableShiftsAccordion from './ProposableShiftsAccordion';
import { handleChangeProposalPartial } from '../../EmployeeDashboard/Utils/PartialShiftHelper';
import './EmployeeProposableShifts.scss';
import { EmployeeFilterMenu } from '../core';
import { Button } from 'rsuite';
import { useTranslation } from 'react-i18next';

/**
 * @typedef {import('moment-timezone').Moment} Moment
 * @typedef {{ firstName: string, lastName: string, employeeId: number }} Employee
 * @typedef {{ qualifier: string, id: number }} OrgJobRef
 * @typedef {{ type: string, orgJobRef: OrgJobRef }} Segment
 * @typedef {{ startDate: string, endDate: string, id: number, segments: Segment[] }} Shift
 * @typedef {{ employee: Employee, shifts: Shift[] }} SelectedProposableShiftsInformation
 * @typedef {{ startDate: Moment?, endDate: Moment?, partialShiftLabel: string }} IProposalPartialDates
 * @param {{
 *  getEmployeeProposableShifts: (number) => Shift[],
 *  selectProposablePrivateEmployee: Function,
 *  updateCheckedProposableShifts: Function,
 *  selectedProposableShiftsByOfferor: SelectedProposableShiftsInformation,
 *  updateProposalPartialDates: Function,
 *  proposalPartialDates: IProposalPartialDates,
 *  offeredShiftsCount: number,
 *  onClick: (Employee) => void,
 * }} props
 */
function EmployeeProposableShifts(props){
    const {
        employeesToOfferList,
        isPrivateEmployeeValidationLoading,
        isPrivateEmployeeNamesLoading,
        isShowWarnings,
        handleFilterChange = () => {},
        getEmployeeProposableShifts,
        selectProposablePrivateEmployee,
        updateCheckedProposableShifts,
        selectedProposableShiftsByOfferor,
        updateProposalPartialDates,
        proposalPartialDates,
        offeredShiftsCount,
        onClick = () => {},
        isPartialShiftEnabled,
        handleOpenDrawer,
        isSizeSmall = false,
        height = 420,
    } = props;
    const { t } = useTranslation('employmentBillboard');
    const { employee, shifts } = selectedProposableShiftsByOfferor;
    /**
     * @type {[Employee[], Function]}
     */
    const [filteredEmployees, setFilteredEmployees] = useState(employeesToOfferList?.employees.map(x => ({ ...x, rowHeight: 75 })) || []);
    const [searchString, setSearchString] = useState("");
    const listRef = React.useRef(null);
    /**
     * @param {Employee} employeeSelected 
     */
    const handleRadioClick = (employeeSelected) => {
        if(employee.employeeId === employeeSelected.employeeId){
            return;
        }
        selectProposablePrivateEmployee(employeeSelected)
        updateCheckedProposableShifts([]);
    }    
    
    /**
    * @param {boolean} checked 
    * @param {Shift} shift 
    * @param {Employee} selectedEmployee
    */
   const handleShiftChecked = (checked, shift, selectedEmployee) => {
       if (checked) {
           if (employee.employeeId === selectedEmployee.employeeId) {
               const updatedshifts = [...shifts, shift];
               updateCheckedProposableShifts(updatedshifts);
           }
           else {
               selectProposablePrivateEmployee(selectedEmployee);
               updateCheckedProposableShifts([shift]);
           }
       }
       else {
           const updatedShiftIds = shifts.filter(x => x.id !== shift.id);
           updateCheckedProposableShifts(updatedShiftIds);
       }
    };

    /**
     * @param {string} employeeIdString
     * @returns {Employee}
     */
    const findEmployeeFromId = (employeeIdString) => {
        return employeesToOfferList.employees.find(x => x.employeeId === employeeIdString);
    }


    useEffect(() => {
        if (!employee.employeeId && employeesToOfferList.employees.length && employeesToOfferList.employees.find(x => x.isValid)) {
            handleRadioClick(employeesToOfferList.employees.find(x => x.isValid));
        }
    }, []);

    useEffect(() => {
        let employees = employeesToOfferList.employees;
        if (!isShowWarnings) {
            employees = employees.filter(x => x.isValid);
        }
        employees.forEach(x => {
            const filteredEmployee = filteredEmployees.find(y => y.employeeId === x.employeeId);
            x.rowHeight = !!filteredEmployee ? filteredEmployee.rowHeight : 75;
            x.expanded = !!filteredEmployee ? filteredEmployee.expanded : false;
        });
        setFilteredEmployees(employees);
        if (listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [employeesToOfferList.employees, isShowWarnings]);
    
    
    const handleSearchInputChange = async (value) => {
        setSearchString(value);
    };

    const onClickClear = () => {
        setSearchString("");
        handleFilterChange({ searchString: "" });
    }
    
    const onClickSearch = async (searchString) => {
        handleFilterChange({ searchString })
    }

    const searchBar = (
        <Paper style={{ width: '100%', display: 'flex', height: '48px', justifyContent: 'space-between' }} elevation={1}>
            <InputBase
                onChange={(event) => handleSearchInputChange(event.target.value)}
                style={{ width: '89%', paddingLeft: '20px' }}
                placeholder={'Search'}
                value={searchString}
                onKeyDown={(event) => {
                    if (event.key === 'Enter')
                        onClickSearch(searchString);
                    }}
            />
            <Button className='sms-clear-btn' hidden={!searchString} onClick={onClickClear}>{t('employeeSidePanel.privateEmployeeFilters.clearBtnLabel')}</Button>
            <Button className='sms-search-btn' onClick={() => onClickSearch(searchString)} disabled={!searchString}><SearchIcon /></Button>
        </Paper>
    );


    const filterButton = (
        <EmployeeFilterMenu onFilterChange={(filters) => handleFilterChange(filters)} t={t} />
    );

    const getItemSize = (index) => {
        return filteredEmployees[index].rowHeight ?? 75;
    }

    const onClickExpand = async (employeeId) => {
        var employeeIndex = filteredEmployees.findIndex(x => x.employeeId == employeeId);
        var employee = filteredEmployees[employeeIndex];
        var prevIsExpanded = employee.expanded;
        var expanded = !prevIsExpanded;
        if (expanded) {
            const eligibleEmployeeResponse = await getEmployeeProposableShifts(employeeId);
            employee.proposableShifts = eligibleEmployeeResponse?.shifts || [];
            employee.isEmployeeCanMakePartials = eligibleEmployeeResponse?.isEmployeeCanMakePartials;
            const hasPartialSection = employee.isEmployeeCanMakePartials && offeredShiftsCount == 1;

            let height = calculateHeightFromShifts(eligibleEmployeeResponse?.shifts, hasPartialSection);
            if (eligibleEmployeeResponse?.shifts.length === 0) {
                height += 45; // Space to show "no valid shifts"
            }
            employee.rowHeight = height;
        }
        else {
            employee.rowHeight = 75;
            employee.isSkipValidation = false;
        }
        employee.expanded = expanded;
        setFilteredEmployees([...filteredEmployees]);
        listRef.current.resetAfterIndex(employeeIndex);
    }

    const onClickShowAll = async (employeeId, isSkipValidation) => {
        var employeeIndex = filteredEmployees.findIndex(x => x.employeeId == employeeId);
        var employee = filteredEmployees[employeeIndex];
        const eligibleEmployeeResponse = await getEmployeeProposableShifts(employeeId, isSkipValidation);
        employee.isSkipValidation = isSkipValidation;
        employee.proposableShifts = eligibleEmployeeResponse?.shifts || [];
        employee.isEmployeeCanMakePartials = eligibleEmployeeResponse?.isEmployeeCanMakePartials;
        const hasPartialSection = employee.isEmployeeCanMakePartials && offeredShiftsCount == 1;

        let height = calculateHeightFromShifts(eligibleEmployeeResponse?.shifts, hasPartialSection);
        if (eligibleEmployeeResponse?.shifts.length === 0) {
            height += 45; // Space to show "no valid shifts"
        }
        employee.rowHeight = height;
        setFilteredEmployees([...filteredEmployees]);
        listRef.current.resetAfterIndex(employeeIndex);
    }
    
    const calculateHeightFromShifts = (shifts, hasPartialSection) => {
        const charsPerLine = isSizeSmall ? 34 : 67;
        let height = 75 + 8 + 16; // Employee name + top and bot padding of list
        if(!shifts.length){
            return height;
        }
        if(shifts.length && hasPartialSection){
            height += 148;
        }
        shifts.forEach(shift => {
            let shiftHeight = 94.016 + 5;// shift card minus segment content + 5 top margin
            shift.segments.forEach(segment => {
                shiftHeight += 20.02 // segment info eg. "1. 07:00 [8.50h] Transfer (x)"
                const lines = Math.ceil((segment.shortOrgJobPath?.length ?? 0) / charsPerLine);
                shiftHeight += (20.02 * lines) + 2; // org job lines + 2 padding
                if (segment.workruleRef?.qualifier && segment.workruleRef?.qualifier !== segment.primaryWorkruleRef?.qualifier) {
                    const workRuleRefLines = Math.ceil((segment.workruleRef?.qualifier.length ?? 0) / charsPerLine);
                    shiftHeight += (20.02 * workRuleRefLines) + 2;// workRule lines + 2 padding
                }
                if (segment.costCenter?.costCenter.label) {
                    const costCenterLines = Math.ceil((segment.costCenter?.costCenter.qualifier.length ?? 0) / charsPerLine);
                    shiftHeight += (20.02 * costCenterLines) + 2;// cost center lines + 2 padding
                }
            });
            height += shiftHeight;
        });
        return height;
    }
    
    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', paddingBottom: '5px' }}>
                <div style={{ flexGrow: 50 }}>
                    {searchBar}
                </div>
                <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {filterButton}
                </div>
            </div>
            <div className="eps-list" style={{ height: `${height}px` }}>
                {isPrivateEmployeeNamesLoading
                    ? <div className='eps-list-loading'><CircularProgress /></div>
                    : filteredEmployees.length > 0
                        ? (<AutoSizer>
                            {({ height, width }) => {
                                return (
                                    <VariableSizeList
                                        ref={listRef}
                                        className="List"
                                        height={height}
                                        width={width}
                                        itemCount={filteredEmployees.length}
                                        itemData={filteredEmployees.map(x => ({
                                            ...x,
                                            getEmployeeProposableShifts,
                                            onClickExpand,
                                            onShiftChecked: handleShiftChecked,
                                            onClick,
                                            onClickShowAll,
                                            handleChangeProposalPartial: (e, val, shifts, requestType) => handleChangeProposalPartial(updateProposalPartialDates, e, val, shifts, requestType),
                                            handleOpenDrawer,
                                            proposableShifts: x.proposableShifts ?? [],
                                            isEmployeeCanMakePartials: x.isEmployeeCanMakePartials ?? false,
                                            expanded: x.expanded ?? false,
                                            rowHeight: x.rowHeight ?? 75,
                                            isPartialShiftEnabled,
                                            offeredShiftsCount,
                                            isSkipValidation: x.isSkipValidation ?? false,
                                        })).map(x => ({
                                            ...x, 
                                            onClickRadioBtn: (e) => handleRadioClick(x),
                                        }))}
                                        itemSize={getItemSize}
                                    >
                                        {renderListItem}
                                    </VariableSizeList>
                                )
                            }}
                            </AutoSizer>)
                        : <div hidden={isPrivateEmployeeValidationLoading} style={{ color: '#999', paddingTop: 20, textAlign: 'center', fontStyle: 'italic' }}>{t('employeeSidePanel.privateEmployeeSection.noEmployeesLabel')}</div>
                    }
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                <span style={{ display: 'flex', gap: '5px' }}>
                    Validated employees: { employeesToOfferList.employees.filter(x => x.isValidated).length }
                    {isPrivateEmployeeValidationLoading ? <CircularProgress size={14} /> : null}
                </span>
                <span>
                    Total employees: { employeesToOfferList.employees.length }
                </span>
            </div>
        </>
    )
}
const renderListItem = (props) => {
    const { index, style, data } = props;
    const item = data[index];
    
    return (
        <div key={`${item.employeeId}`} style={{...style, justifyContent: 'space-between'}}>
            <ProposableShiftsAccordion
                key={`${item.employeeId}-ProposableShiftsAccordion`}
                employee={item}
            />
        </div>
)};
 
const mapStateToProps = state => {
    return {
        selectedProposableShiftsByOfferor: state.selectedProposableShiftsByOfferor,
        proposalPartialDates: state.proposalPartialDates,
        employeesToOfferList: state.employeesToOfferList,
        isPrivateEmployeeValidationLoading: state.privateEmployeeValidationLoadingState,
        isPrivateEmployeeNamesLoading: state.privateEmployeeNamesLoadingState,
    }
}

const mapDispatchToProps = dispatch => {
    return {
        selectProposablePrivateEmployee: payload => dispatch(selectProposablePrivateEmployee(payload)),
        updateCheckedProposableShifts: payload => dispatch(updateCheckedProposableShifts(payload)),
        updateProposalPartialDates: payload => dispatch(updateProposalPartialDates(payload)),
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(EmployeeProposableShifts);
